import { pgTable, text, serial, integer, boolean, timestamp } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const users = pgTable("users", {
  id: serial("id").primaryKey(),
  username: text("username").notNull().unique(),
  password: text("password").notNull(),
  email: text("email").notNull(),
  first_name: text("first_name").notNull(),
  last_name: text("last_name").notNull(),
  freeSessionAvailable: boolean("free_session_available").default(true),
  freeSessionResetDate: timestamp("free_session_reset_date"),
  stripeCustomerId: text("stripe_customer_id"),
  stripeSubscriptionId: text("stripe_subscription_id"),
});

export const therapists = pgTable("therapists", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  title: text("title").notNull(),
  bio: text("bio").notNull(),
  image_url: text("image_url").notNull(),
  hourly_rate: integer("hourly_rate").notNull(),
  rating: integer("rating").notNull(),
  review_count: integer("review_count").notNull(),
  next_availability: timestamp("next_availability"),
});

export const specialties = pgTable("specialties", {
  id: serial("id").primaryKey(),
  name: text("name").notNull().unique(),
});

export const therapistSpecialties = pgTable("therapist_specialties", {
  id: serial("id").primaryKey(),
  therapistId: integer("therapist_id").notNull(),
  specialtyId: integer("specialty_id").notNull(),
});

export const sessions = pgTable("sessions", {
  id: serial("id").primaryKey(),
  userId: integer("user_id").notNull(),
  therapistId: integer("therapist_id").notNull(),
  date: timestamp("date").notNull(),
  isFree: boolean("is_free").notNull(),
  status: text("status").notNull(), // "scheduled", "completed", "cancelled"
  paymentIntentId: text("payment_intent_id"),
  meetingLink: text("meeting_link"),
});

export const availabilities = pgTable("availabilities", {
  id: serial("id").primaryKey(),
  therapistId: integer("therapist_id").notNull(),
  date: timestamp("date").notNull(),
  isAvailable: boolean("is_available").notNull().default(true),
});

// Insert schemas
export const insertUserSchema = createInsertSchema(users).omit({
  id: true,
  freeSessionResetDate: true,
  stripeCustomerId: true,
  stripeSubscriptionId: true,
});

export const insertTherapistSchema = createInsertSchema(therapists).omit({
  id: true,
});

export const insertSpecialtySchema = createInsertSchema(specialties).omit({
  id: true,
});

export const insertTherapistSpecialtySchema = createInsertSchema(therapistSpecialties).omit({
  id: true,
});

export const insertSessionSchema = createInsertSchema(sessions).omit({
  id: true,
  paymentIntentId: true,
  meetingLink: true,
});

export const insertAvailabilitySchema = createInsertSchema(availabilities).omit({
  id: true,
});

// Types
export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;

export type InsertTherapist = z.infer<typeof insertTherapistSchema>;
export type Therapist = typeof therapists.$inferSelect;

export type InsertSpecialty = z.infer<typeof insertSpecialtySchema>;
export type Specialty = typeof specialties.$inferSelect;

export type InsertTherapistSpecialty = z.infer<typeof insertTherapistSpecialtySchema>;
export type TherapistSpecialty = typeof therapistSpecialties.$inferSelect;

export type InsertSession = z.infer<typeof insertSessionSchema>;
export type Session = typeof sessions.$inferSelect;

export type InsertAvailability = z.infer<typeof insertAvailabilitySchema>;
export type Availability = typeof availabilities.$inferSelect;

// Extended types for frontend
export type TherapistWithSpecialties = Therapist & {
  specialties: Specialty[];
};